// Shared logout/unload teardown primitives (used by the signout thunks and the
// beforeunload handler — keep the steps in sync in one place).
//
// A teardown runs in three acts, and the split is deliberate: everything visible dies at once,
// the wipes then run behind that blackout, and only at the very end is the document itself
// destroyed and the tab sent away. Killing the whole document up front would look identical to
// the user and cost the wipes their channels into the embedded apps.

import { redirectToSafePage } from './redirect';
import { sleep } from './sleep';

export const SIGNOUT_IFRAME_EXIT_CAP_MS = 10_000;
export const PANIC_IFRAME_EXIT_CAP_MS = 3_000;

const TEARDOWN_BG = '#151e27';
const BLACKOUT_ID = 'teardownBlackout';

// Set once a teardown starts and never cleared (a teardown ends by leaving the page). The exit
// page's `waitingInfo` reaches every message listener on the window — the app's own per-frame
// listener as well as the teardown's — and only the teardown's answer carries the panic flag. The
// per-frame one is registered first and would otherwise reply first, without it. This lets that
// listener step aside during a teardown so the exit page hears the panic-aware answer alone.
let teardownActive = false;
export const isTeardownActive = () => teardownActive;

// Ask every embedded external app to wipe its own storages. They live on a different origin, so a
// postMessage signal is the only way to reach them — and their injected tools clear localStorage,
// sessionStorage, cookies and every IndexedDB on their own, without needing the network.
export const signalIframesCleanup = () => {
  document.querySelectorAll('iframe').forEach((iframe) => {
    if (iframe.src && iframe.contentWindow) {
      try {
        iframe.contentWindow.postMessage('cleanup', '*');
      } catch (_err) {
        // Ignore errors
      }
    }
  });
};

// Every foreign origin currently embedded here.
export const collectExternalOrigins = () => {
  const origins = new Set();
  document.querySelectorAll('iframe').forEach((iframe) => {
    try {
      const { origin } = new URL(iframe.src, location.href);
      if (origin !== location.origin) origins.add(origin);
    } catch (_err) {
      // Unusable src — nothing to wipe
    }
  });
  return [...origins];
};

// Fire-and-forget ask for each origin to clear itself, over the network. /exitExternalApp answers
// with `Clear-Site-Data: "*"` in prod, and both transports here are built to outlive the page —
// which is the whole point: on a closing tab there is no time for a frame to load and report back.
// Two of them because neither is universal: keepalive fetch is the one that carries cookies as a
// normal request, sendBeacon is the one browsers reliably let through on unload. Sending both is
// harmless — the endpoint just answers twice.
export const requestExternalOriginWipes = (origins) => {
  origins.forEach((origin) => {
    const url = `${origin}/exitExternalApp`;
    try {
      navigator.sendBeacon?.(url);
    } catch (_err) {
      // Ignore errors
    }
    try {
      fetch(url, {
        mode: 'no-cors',
        credentials: 'include',
        cache: 'no-store',
        keepalive: true,
      }).catch(() => {});
    } catch (_err) {
      // Ignore errors
    }
  });
};

// Drop every node except the iframes and the ancestors they hang from — the wipe of the embedded
// apps runs inside those frames, so they have to stay in the DOM and unmoved. Removal happens at
// the topmost node possible, so a live UI tree is detached whole and keeps its internal structure;
// the renderer can go on patching it off-document without errors.
const stripToIframes = (root) => {
  [...root.childNodes].forEach((node) => {
    if (node.nodeName === 'IFRAME') return;
    if (node.querySelector?.('iframe')) stripToIframes(node);
    else node.remove();
  });
};

// Act one, synchronous: nothing readable is left on screen or in the DOM. The surviving frames are
// hidden and covered by an opaque blackout — three layers, because each covers a hole in the
// others: the root background survives the later wipe, hiding the body kills every descendant
// (including anything in the top layer), and the blackout covers whatever re-declares itself
// visible underneath. The tab is anonymised too: its name and icon are traces of their own.
export const hideDocumentForTeardown = () => {
  teardownActive = true;
  document.documentElement.style.backgroundColor = TEARDOWN_BG;
  document.body.style.visibility = 'hidden';
  document.title = '';
  document.head.querySelectorAll('link[rel*="icon"]').forEach((link) => {
    link.remove();
  });

  stripToIframes(document.body);

  const blackout = document.createElement('div');
  blackout.id = BLACKOUT_ID;
  blackout.style.cssText = `position:fixed;inset:0;background:${TEARDOWN_BG};z-index:2147483647;visibility:visible`;
  document.body.appendChild(blackout);
};

// Act two: navigate every external-app iframe to its origin's /exitExternalApp. In prod nginx
// answers it with `Clear-Site-Data: "*"`, and the page itself (src/external/exit.html) runs the
// injected storage wipe — the two paths cover each other, and the page is prefetched, so it also
// works offline. We wait for the page's own "done wiping" report rather than its load event: a
// frame dropped on load would be dropped mid-wipe. The cap keeps a dead origin from holding the
// exit hostage; a panic is on the shorter leash and also tells the exit page to skip its session
// backup.
export const exitExternalAppIframes = (capMs, { panic = false } = {}) => {
  const iframes = [...document.querySelectorAll('iframe.externalAppInstanceIframe')];
  if (!iframes.length) return Promise.resolve();

  const exits = iframes.map(
    (iframe) =>
      new Promise((resolve) => {
        let url;
        try {
          url = new URL('/exitExternalApp', iframe.src);
        } catch (_err) {
          resolve();
          return;
        }
        const finish = () => {
          window.removeEventListener('message', onMessage);
          resolve();
        };
        const onMessage = (event) => {
          if (event.origin !== url.origin) return;
          // The exit page asks the opener for what it needs, and we answer here — from the teardown
          // itself, since the app component around the frame may already be unmounted. Nothing goes
          // in the URL, where it would land in proxy logs and history: `panic` tells the page to
          // skip its session backup, `domain` lets it pull that service's own exit script (which
          // ends the session on the service's side, beyond wiping storage).
          if (event.data === 'waitingInfo') {
            try {
              iframe.contentWindow?.postMessage(
                { type: 'info', panic, domain: iframe.dataset.appDomain || null },
                url.origin
              );
            } catch (_err) {
              // The frame may already be gone; the wipe itself does not depend on this.
            }
            return;
          }
          if (event.data === 'closeModal') finish();
        };

        window.addEventListener('message', onMessage);
        iframe.addEventListener('error', finish, { once: true });
        iframe.src = url.href;
      })
  );

  return Promise.race([Promise.all(exits), sleep(capMs)]);
};

// Act three: destroy the document — head and body alike, nothing exempt — and leave. The
// navigation is part of the wipe, not just a redirect: it is what finally kills the tab's memory.
// Panic paths land on the neutral safe page instead of the login screen.
export const wipeDocumentAndReload = ({ safePage = false } = {}) => {
  document.head.replaceChildren();
  document.body.replaceChildren();
  document.body.style.cssText = `background-color:${TEARDOWN_BG}`;
  if (safePage) {
    // Panic exit: from an installed PWA prefer closing the window outright; else a neutral page.
    redirectToSafePage({ tryClose: true });
  } else {
    window.location.replace('/');
  }
};
